import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, type FlowStep, type FlowStepType } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { CopyCodeBlock, Switch } from '../components/ui.js';
import { Topbar } from './AdminLayout.js';
import { isolate } from '../utils/bidi.js';

// Шаги, которые можно добавить. Порядок = порядок в выпадающем списке.
const STEP_TYPES: FlowStepType[] = ['sendpulse_email', 'smtp_email', 'issue_ticket', 'messenger'];

// Заглушки «на будущее»: после оплаты ничего не делают. Билет выпускается
// настройками продукта, мессенджер ждёт провайдера. В списках шагов они серые
// (disabled) — выбрать нельзя; шаги-заглушки, добавленные раньше, видны серыми.
const PLACEHOLDER_TYPES = new Set<FlowStepType>(['issue_ticket', 'messenger']);

// Пометка в выпадающем списке: почему пункт серый.
const PLACEHOLDER_OPTION_HINTS: Partial<Record<FlowStepType, string>> = {
  issue_ticket: 'включается в товаре',
  messenger: 'пока не работает',
};

const optionLabel = (t: FlowStepType): string =>
  PLACEHOLDER_OPTION_HINTS[t] ? `${STEP_LABELS[t]} — ${PLACEHOLDER_OPTION_HINTS[t]}` : STEP_LABELS[t];

// Шаги старых флоу. В списке добавления их нет, но существующие строки надо
// показать — иначе оператор не поймёт, почему в флоу «пусто», а шагов 4.
const RETIRED_TYPES = new Set<FlowStepType>([
  'notion_order',
  'telegram_operator',
  'sendpulse_book',
]);

const STEP_LABELS: Record<FlowStepType, string> = {
  sendpulse_email: 'Письмо клиенту (SendPulse)',
  smtp_email: 'Письмо клиенту (SMTP установки)',
  issue_ticket: 'Выпустить билет',
  messenger: 'Сообщение в мессенджер (TG / WhatsApp)',
  telegram_operator: 'Уведомить оператора (Telegram)',
  notion_order: 'Карточка заказа в Notion',
  sendpulse_book: 'Добавить в книгу SendPulse',
};

const PLACEHOLDER_NOTES: Partial<Record<FlowStepType, string>> = {
  issue_ticket:
    'Пока не работает. Билеты выпускаются настройками продукта — этот шаг ни на что не влияет, он зарезервирован на будущее.',
  messenger:
    'Пока не работает. Сообщение уйдёт в тот мессенджер, который клиент оставил на чекауте, когда подключим ботов.',
};

type Draft = { type: FlowStepType; config: Record<string, unknown>; isActive: boolean };

const cfgStr = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Готовый HTML кнопок билета для своего шаблона: вставить в шаблон SendPulse
 * (редактор → блок «HTML») или в HTML шага SMTP. Ссылки — переменные флоу;
 * они есть только у продуктов с билетами и заполненным событием.
 */
function TicketButtonsSnippet(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!open || html) return;
    api
      .ticketButtonsHtml()
      .then((r) => setHtml(r.html))
      .catch((e: Error) => setErr(e.message));
  }, [open, html]);
  return (
    <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>
      <button type="button" className="btn btn-sm btn-ghost" style={{ paddingLeft: 0 }} onClick={() => setOpen((v) => !v)}>
        {open ? 'Скрыть кнопки билета' : 'Кнопки билета для шаблона'}
      </button>
      {open && (
        <div className="stack" style={{ gap: 6 }}>
          <div>
            «Скачать билет», «Add to Apple Wallet», «Add to Google Wallet» и «Добавить в календарь». Нажмите на код,
            чтобы скопировать, и вставьте в шаблон SendPulse блоком «HTML» (или в HTML письма SMTP). Шаблон с
            кнопками — только для продуктов с билетами и заполненным «Событием билета»: у остальных ссылок нет.
            Кнопки кошельков работают, когда в «Настройках» включён Wallet.
          </div>
          {err && <div style={{ color: 'var(--danger-fg)' }}>{err}</div>}
          {html && <CopyCodeBlock text={html} />}
        </div>
      )}
    </div>
  );
}

export function FlowEdit(): JSX.Element {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [templates, setTemplates] = useState<{ id: string; name: string }[] | null>(null);

  const [activeProfileId] = useActiveProfileId();
  const [initialProfileId] = useState(activeProfileId);
  useEffect(() => {
    if (initialProfileId !== null && activeProfileId !== initialProfileId) {
      nav('/admin/flows', { replace: true });
    }
  }, [activeProfileId, initialProfileId, nav]);

  useEffect(() => {
    api.listSendpulseTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, [activeProfileId]);

  useEffect(() => {
    if (isNew) return;
    api
      .getFlow(id!)
      .then((f) => {
        setName(f.name);
        setIsActive(f.isActive);
        setSteps((f.steps ?? []).map((s: FlowStep) => ({ type: s.type, config: s.config ?? {}, isActive: s.isActive })));
        setLoading(false);
      })
      .catch((e: Error) => {
        setErr(e.message);
        setLoading(false);
      });
  }, [id, isNew]);

  const patchStep = (i: number, patch: Partial<Draft>): void =>
    setSteps((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const patchConfig = (i: number, c: Record<string, unknown>): void =>
    setSteps((arr) => arr.map((s, idx) => (idx === i ? { ...s, config: { ...s.config, ...c } } : s)));
  const addStep = (type: FlowStepType): void =>
    setSteps((arr) => [...arr, { type, config: {}, isActive: true }]);
  const removeStep = (i: number): void => setSteps((arr) => arr.filter((_, idx) => idx !== i));
  const moveStep = (i: number, dir: -1 | 1): void =>
    setSteps((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });

  // Активный шаг «Письмо клиенту» без шаблона отменял бы встроенное письмо и
  // при этом ничего не отправлял — клиент не получил бы вообще ничего. Не даём
  // сохранить такой флоу.
  const brokenEmailSteps = steps
    .map((s, idx) => ({ s, idx }))
    .filter(
      ({ s }) =>
        s.isActive &&
        ((s.type === 'sendpulse_email' && !cfgStr(s.config.templateId)) ||
          (s.type === 'smtp_email' && !cfgStr(s.config.html))),
    )
    .map(({ idx }) => idx);
  const hasBrokenEmail = brokenEmailSteps.length > 0;

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const body = { name, isActive, steps: steps.map((s) => ({ type: s.type, config: s.config, isActive: s.isActive })) };
    try {
      if (isNew) {
        const created = await api.createFlow(body);
        nav(`/admin/flows/${created.id}`, { replace: true });
      } else {
        await api.updateFlow(id!, body);
        nav('/admin/flows');
      }
    } catch (e) {
      const d = (e as { details?: unknown }).details;
      setErr(typeof d === 'string' ? d : JSON.stringify(d));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!id || isNew) return;
    if (!confirm('Удалить флоу? Продукты, где он выбран, вернутся к дефолтному сценарию.')) return;
    await api.deleteFlow(id);
    nav('/admin/flows');
  };

  if (loading) {
    return (
      <>
        <Topbar title="Флоу" />
        <div style={{ padding: 32, color: 'var(--fg-subtle)' }}>Загрузка…</div>
      </>
    );
  }

  return (
    <>
      <Topbar title={isNew ? 'Новый флоу' : <bdi>{name || 'Флоу'}</bdi>} />
      <form onSubmit={onSubmit} style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        <div style={{ marginBottom: 14, fontSize: 13 }}>
          <Link to="/admin/flows" style={{ textDecoration: 'underline' }}>← Все флоу</Link>
        </div>
        <div style={{ display: 'grid', gap: 14, maxWidth: 760 }}>
          <div className="card">
            <div className="card-header"><div className="card-title">Основное</div></div>
            <div className="card-body stack">
              <div className="field">
                <label className="field-label">Название *</label>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Например, «После покупки клуба»" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>Активен</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>Выключенный флоу не выполняется (продукт идёт по дефолту)</div>
                </div>
                <Switch checked={isActive} onChange={setIsActive} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="card-title">Шаги ({steps.length})</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  className="select"
                  style={{ width: 'auto' }}
                  value=""
                  onChange={(e) => { if (e.target.value) addStep(e.target.value as FlowStepType); }}
                >
                  <option value="">+ Добавить шаг…</option>
                  {STEP_TYPES.map((t) => (
                    <option key={t} value={t} disabled={PLACEHOLDER_TYPES.has(t)}>{optionLabel(t)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="card-body stack">
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--fg-subtle)',
                  background: 'var(--bg-subtle)',
                  border: '1px solid var(--hairline)',
                  borderRadius: 'var(--radius)',
                  padding: '8px 10px',
                }}
              >
                Билет, карточка в Notion, уведомление оператора и книга SendPulse отрабатывают
                <b> до </b>
                этих шагов — они настраиваются в продукте. Флоу отвечает только за то, что уходит
                клиенту.
              </div>
              {steps.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--fg-subtle)', padding: '8px 0' }}>
                  Шагов нет — клиенту после оплаты ничего не уйдёт (продукт отработает как обычно).
                </div>
              )}
              {steps.map((step, i) => (
                <div
                  key={i}
                  style={{
                    border: `1px solid ${brokenEmailSteps.includes(i) ? 'var(--danger-fg)' : 'var(--hairline)'}`,
                    borderRadius: 'var(--radius)',
                    padding: 12,
                    background:
                      step.isActive && !PLACEHOLDER_TYPES.has(step.type) && !RETIRED_TYPES.has(step.type)
                        ? 'var(--surface)'
                        : 'var(--bg-subtle)',
                    opacity:
                      PLACEHOLDER_TYPES.has(step.type) || RETIRED_TYPES.has(step.type)
                        ? 0.55
                        : step.isActive
                          ? 1
                          : 0.7,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--fg-subtle)', width: 18 }}>{i + 1}</span>
                    <select
                      className="select"
                      style={{ flex: 1 }}
                      value={step.type}
                      onChange={(e) => patchStep(i, { type: e.target.value as FlowStepType, config: {} })}
                    >
                      {STEP_TYPES.map((t) => (
                        <option key={t} value={t} disabled={PLACEHOLDER_TYPES.has(t) && t !== step.type}>{optionLabel(t)}</option>
                      ))}
                      {RETIRED_TYPES.has(step.type) && (
                        <option value={step.type}>{STEP_LABELS[step.type]} (устарел)</option>
                      )}
                    </select>
                    <button type="button" className="btn btn-sm btn-ghost" title="Вверх" onClick={() => moveStep(i, -1)} disabled={i === 0}>↑</button>
                    <button type="button" className="btn btn-sm btn-ghost" title="Вниз" onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1}>↓</button>
                    <Switch checked={step.isActive} onChange={(v) => patchStep(i, { isActive: v })} />
                    <button type="button" className="btn btn-sm btn-ghost btn-danger" title="Удалить" onClick={() => removeStep(i)}>✕</button>
                  </div>

                  {step.type === 'sendpulse_email' && (
                    <div className="stack" style={{ marginTop: 10 }}>
                      <div className="field">
                        <label className="field-label">Шаблон SendPulse</label>
                        <select
                          className="select"
                          value={cfgStr(step.config.templateId)}
                          onChange={(e) => {
                            const t = templates?.find((x) => x.id === e.target.value);
                            patchConfig(i, { templateId: e.target.value, templateName: t?.name ?? '' });
                          }}
                        >
                          <option value="">{templates ? '— выбери шаблон —' : 'Загрузка…'}</option>
                          {templates?.map((t) => <option key={t.id} value={t.id} dir="auto">{t.name}</option>)}
                          {cfgStr(step.config.templateId) && !templates?.some((t) => t.id === cfgStr(step.config.templateId)) && (
                            <option value={cfgStr(step.config.templateId)}>{isolate(cfgStr(step.config.templateName) || cfgStr(step.config.templateId))} (недоступен)</option>
                          )}
                        </select>
                      </div>
                      <div className="field">
                        <label className="field-label">Тема письма <span style={{ fontWeight: 400, color: 'var(--fg-subtle)' }}>— если пусто: «Спасибо за покупку!»; можно {'{{ticket_id}}'} и другие переменные</span></label>
                        <input className="input" value={cfgStr(step.config.subject)} onChange={(e) => patchConfig(i, { subject: e.target.value })} placeholder="Спасибо за покупку!" />
                      </div>
                      <div className="field">
                        <label className="field-label">Скрытая копия <span style={{ fontWeight: 400, color: 'var(--fg-subtle)' }}>— кому ещё прислать копию каждого письма; несколько адресов через запятую</span></label>
                        <input className="input" value={cfgStr(step.config.bcc)} onChange={(e) => patchConfig(i, { bcc: e.target.value })} placeholder="owner@example.com" />
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>
                        Переменные в шаблоне: <span className="mono">{'{{name}} {{product}} {{amount}} {{currency}} {{ticket_id}} {{ticket_url}} {{wallet_apple_url}} {{wallet_google_url}} {{calendar_url}}'}</span>
                      </div>
                      <TicketButtonsSnippet />
                    </div>
                  )}

                  {step.type === 'smtp_email' && (
                    <div className="stack" style={{ marginTop: 10 }}>
                      <div className="field">
                        <label className="field-label">Тема письма</label>
                        <input className="input" value={cfgStr(step.config.subject)} onChange={(e) => patchConfig(i, { subject: e.target.value })} placeholder="Спасибо за покупку!" />
                      </div>
                      <div className="field">
                        <label className="field-label">HTML письма</label>
                        <textarea className="input mono" rows={12} value={cfgStr(step.config.html)} onChange={(e) => patchConfig(i, { html: e.target.value })} placeholder="<!DOCTYPE html>…" />
                        <div className="field-hint">
                          Отправляется через SMTP установки (env SMTP_*), без SendPulse. Переменные:{' '}
                          <span className="mono">{'{{name}} {{product}} {{amount}} {{currency}} {{ticket_id}} {{ticket_url}} {{wallet_apple_url}} {{wallet_google_url}} {{calendar_url}}'}</span>
                        </div>
                        <TicketButtonsSnippet />
                      </div>
                    </div>
                  )}

                  {PLACEHOLDER_TYPES.has(step.type) && (
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--fg-subtle)' }}>
                      {PLACEHOLDER_NOTES[step.type]}
                    </div>
                  )}

                  {RETIRED_TYPES.has(step.type) && (
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--fg-subtle)' }}>
                      Устаревший шаг — не выполняется. Это действие настраивается в продукте и
                      происходит до флоу. Можно удалить.
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {hasBrokenEmail && (
            <div
              className="card"
              style={{ padding: 14, color: 'var(--danger-fg)', background: 'var(--danger-subtle-bg)' }}
            >
              В шаге «Письмо клиенту» не выбран шаблон
              {brokenEmailSteps.length > 1 ? ` (шаги ${brokenEmailSteps.map((i) => i + 1).join(', ')})` : ` (шаг ${brokenEmailSteps[0]! + 1})`}.
              Пока шаблона нет, письмо не уйдёт — выбери его или выключи шаг.
            </div>
          )}

          {err && (
            <div className="card" style={{ padding: 14, color: 'var(--danger-fg)', background: 'var(--danger-subtle-bg)' }}>{err}</div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="submit"
              disabled={busy || !name.trim() || hasBrokenEmail}
              className="btn btn-primary"
              title={hasBrokenEmail ? 'Выбери шаблон в шаге «Письмо клиенту»' : undefined}
            >
              {busy ? 'Сохраняем…' : 'Сохранить'}
            </button>
            {!isNew && (
              <button type="button" onClick={onDelete} className="btn btn-danger">Удалить</button>
            )}
          </div>
        </div>
      </form>
    </>
  );
}
